import React, { useEffect, useState } from 'react';
import { RouteComponentProps } from '@reach/router';
import { RFC } from 'state/StateTypes';
import isAuthenticated from 'hooks/isAuthenticated';
import ViewContainer from 'components/ViewContainer';
import DetailHeader from 'components/DetailHeader';
import BackButton from 'components/BackButton';
import Loading from 'components/Loading';
import { InfoList, Info } from 'components/InfoList';
import Label from 'components/Label';
import ErrorMessage from 'components/ErrorMessage';
import styled from 'styled-components';
import { User, PromoEligibility } from 'state/user/UserTypes';
import { forgotPassword, getUser, updateUser } from 'state/user/UserActions';
import { Error, ReactSelectValue, ReactSelectOption, ErrorCodes } from 'types';
import { deriveDateFromMongoId, convertTimeStamp, determineUserTier, dateIsAfterToday, presentErrorBasedOnCode } from 'utils';
import Subscription from 'components/Subscription';
import Select from 'components/Select';
import FlexWrapper from 'components/FlexWrapper';
import Modal from 'components/Modal';
import { SubscriptionProviders } from 'state/subscription/SubscriptionTypes';
import Switch from 'components/Switch';
import ToastAlert from 'components/ToastAlert';
import SmallLoader from 'components/SmallLoader';
import ToggleButton from 'components/CustomerToggleButton';
import { useDispatch } from 'react-redux';
import { SET_IS_VIEWING_USER_DETAILS } from 'state/users/UsersActionTypes';
import ModalButton from 'components/ModalButton';
interface Props extends RouteComponentProps {
   id?: string;
}

const CustomerDetail: RFC<Props> = ({ id }) => {
   const [customer, setCustomer] = useState<User>();
   const [loading, setLoading] = useState<boolean>(true);
   const adminRoleRequired = true;
   const authenticated: boolean = isAuthenticated(adminRoleRequired);
   const [premiumTimeToAdd, setPremiumTimeToAdd] = useState<PromoEligibility>();
   const [modalOpen, setModalOpen] = useState<boolean>(false);
   const [error, setError] = useState<Error | null>(null);
   const [subscriptionExpired, setSubscriptionExpired] = useState<boolean | null>();
   const [updateError, setUpdateError] = useState<Error | null>(null);
   const [showAppleSubAlert, setShowAppleSubAlert] = useState<boolean>(false);
   const [updatingGoogleExpDate, setUpdatingGoogleExpDate] = useState<boolean>(false);
   const [togglingStatus, setTogglingStatus] = useState<boolean>(false);
   const [resettingPassword, setResettingPassword] = useState<boolean>(false);
   const [resetPasswordEmailBeingSent, setResetPasswordEmailBeingSent] = useState<boolean>(false);
   const [showResetPasswordNotif, setShowResetPasswordNotif] = useState<boolean>(false);
   const [customerWithoutSubBeingUpgraded, setCustomerWithoutSubBeingUpgraded] = useState<boolean>(false);
   const dispatch = useDispatch();

   useEffect(() => {
      (async () => {
         if (authenticated && id) {
            try {
               const customer = await getUser(id);
               setCustomer(customer);
               if (!!customer?.subscription.length) {
                  setSubscriptionExpired(dateIsAfterToday(customer.subscription[0].expirationDate));
               } else {
                  setSubscriptionExpired(null);
               }
               dispatch({ type: SET_IS_VIEWING_USER_DETAILS, payload: true });
            } catch (error) {
               //if error results from missing token it will not have a json method
               error = error.json ? await error.json() : error;
               setError(error);
               dispatch({ type: SET_IS_VIEWING_USER_DETAILS, payload: false });
            }
            setLoading(false);
         }
      })();
   }, [id]);

   const handlePremiumTimeSelect = (time: ReactSelectValue) => {
      updateError && setUpdateError(null);
      setPremiumTimeToAdd((time as ReactSelectOption).value as PromoEligibility);
   };

   const closeModal = () => {
      setModalOpen(false);
      setPremiumTimeToAdd(undefined);
   };

   const updateUserSubscription = async (): Promise<void> => {
      updateError && setUpdateError(null);
      try {
         if (customer?.subscription[0]?.paymentProvider === SubscriptionProviders.Google && !!premiumTimeToAdd) {
            await setUpdatingGoogleExpDate(true);
         } else {
            setCustomerWithoutSubBeingUpgraded(true);
         }
         // we know premiumToAdd & customer will be defined here
         const itemToUpdate: { promoEligibility: PromoEligibility } | { premiumOverride: boolean } = premiumTimeToAdd
            ? { promoEligibility: premiumTimeToAdd }
            : { premiumOverride: !customer?.premiumOverride };
         const updatedCustomer = await updateUser(itemToUpdate, customer!._id);
         setCustomer(updatedCustomer);
         await setPremiumTimeToAdd(undefined);
         if (updatedCustomer.subscription[0]?.paymentProvider === SubscriptionProviders.Apple && !!premiumTimeToAdd) {
            setShowAppleSubAlert(true);
            setTimeout(async () => {
               await setShowAppleSubAlert(false);
            }, 4500);
         }
      } catch (error) {
         error = error.json ? await error.json() : error;
         console.log(error);
         setUpdateError(error);
      }
      setUpdatingGoogleExpDate(false);
      setCustomerWithoutSubBeingUpgraded(false);
   };

   const toggleStatus = async (): Promise<void> => {
      try {
         await setTogglingStatus(true);
         const updatedCustomer = await updateUser({ isLegacy: !customer?.isLegacy }, customer!._id);
         setCustomer(updatedCustomer);
      } catch (error) {
         error = error.json ? await error.json() : error;
         console.log(error);
         setUpdateError(error);
      }
      setTogglingStatus(false);
   };

   const resetPassword = async (): Promise<void> => {
      try {
         await setResetPasswordEmailBeingSent(true);
         await forgotPassword({ username: customer?.email! });
         setShowResetPasswordNotif(true);
         setTimeout(async () => {
            await setShowResetPasswordNotif(false);
         }, 4500);
      } catch (error) {
         console.log(error);
         setUpdateError(error);
      }
      setResettingPassword(false);
      setResetPasswordEmailBeingSent(false);
   };

   const IOSVerbiage: React.FC = (): JSX.Element => (
      <>
         You are making this user eligible for <Highlight>{premiumTimeToAdd === PromoEligibility.twoMonths ? 'two' : 'six'} free months</Highlight> of
         premium subscription. The user will need to redeem this offer in app. This action is <b>irreversible</b>. Do you wish to continue?
      </>
   );
   const AndroidVerbiage: React.FC = (): JSX.Element => (
      <>
         You are adding <Highlight>{premiumTimeToAdd === PromoEligibility.twoMonths ? 'two' : 'six'} free months</Highlight> to this customer's
         subscription. This action is <b>irreversible</b>. Do you wish to continue?
      </>
   );
   const ResetPasswordVerbiage: React.FC = (): JSX.Element => <>This user will be sent an email allowing them to reset their password. Proceed?</>;
   const selectOptions = [
      { value: PromoEligibility.twoMonths, label: '2 months' },
      { value: PromoEligibility.sixMonths, label: '6 months' },
   ];
   return (
      <ViewContainer>
         <BackButton to="/customers" />
         {customer && <DetailHeader title="customer detail" loading={loading} />}
         {loading ? (
            <Loading />
         ) : error || !customer ? (
            <ErrorMessage>
               {error ? presentErrorBasedOnCode(error.code as ErrorCodes) : "Something went wrong in retrieving that customer's details!"}
            </ErrorMessage>
         ) : (
            <DetailList>
               <Info>
                  <Label>Name</Label> {customer.firstName} {customer.lastName}
               </Info>
               <Info>
                  <Label>Email</Label> {customer.email}
               </Info>
               <Info>
                  <Label>Phone</Label> {customer.phone}
               </Info>
               <Info>
                  <Label>Created</Label> {deriveDateFromMongoId(customer._id)}
               </Info>
               <Info>
                  <Label>Tier</Label> {determineUserTier(customer)}
               </Info>
               <Info>
                  <Label>Updated</Label> {customer.updatedAt ? convertTimeStamp(customer.updatedAt) : ''}
               </Info>
               <Section>
                  <SectionHeader>Manage account</SectionHeader>
                  <HeaderButtonContainer>
                     <UserActionButton onClick={toggleStatus}>
                        {customer.isLegacy ? (
                           togglingStatus ? (
                              <SmallLoader color="light" size={18} />
                           ) : (
                              'Revoke Loyalty Status'
                           )
                        ) : togglingStatus ? (
                           <SmallLoader color="light" size={18} />
                        ) : (
                           'Grant Loyalty Status'
                        )}
                     </UserActionButton>
                     <UserActionButton
                        onClick={() => {
                           setResettingPassword(true);
                           setModalOpen(true);
                        }}>
                        {resetPasswordEmailBeingSent ? <SmallLoader color="light" size={18} /> : 'Reset password'}
                     </UserActionButton>
                  </HeaderButtonContainer>
               </Section>
               <Section>
                  <SectionHeader>Manage Subscription</SectionHeader>
                  <Info>
                     <Label>Subscription</Label>{' '}
                     {!customer?.subscription.length
                        ? 'No active subscription'
                        : subscriptionExpired && (
                             <ExpiredNotification>
                                This user's {String(customer.subscription[0].paymentProvider)} subscription expired{' '}
                                {convertTimeStamp(customer.subscription[0].expirationDate)}
                             </ExpiredNotification>
                          )}
                     {showAppleSubAlert && (
                        <ToastAlert
                           overlay={true}
                           text="Additional time added successfully. The next time the user opens the iOS app, they will be prompted to accept the additional complimentary premium access."
                           size="large"
                        />
                     )}
                     {showResetPasswordNotif && (
                        <ToastAlert
                           overlay={true}
                           text={`An email has been sent to ${customer?.email} for them to reset their password`}
                           size="large"
                        />
                     )}
                     <Modal isOpen={modalOpen}>
                        <ModalContent>
                           {resettingPassword ? (
                              <ResetPasswordVerbiage />
                           ) : customer.subscription[0]?.paymentProvider === SubscriptionProviders.Apple ? (
                              <IOSVerbiage />
                           ) : (
                              <AndroidVerbiage />
                           )}
                           <FlexWrapper justifyContent="center" margin="3em 0 0" flexWrap="wrap">
                              <ModalButton action="cancel" onClick={closeModal}>
                                 Cancel
                              </ModalButton>
                              <ModalButton
                                 action="confirm"
                                 onClick={() => {
                                    resettingPassword ? resetPassword() : updateUserSubscription();
                                    closeModal();
                                 }}>
                                 {resettingPassword ? 'Reset password' : 'Add time'}
                              </ModalButton>
                           </FlexWrapper>
                        </ModalContent>
                     </Modal>
                  </Info>
                  <SubscriptionContainer>
                     <FlexWrapper flexDirection="column" justifyContent="flex-start" alignItems="flex-start">
                        {!!customer?.subscription.length && (
                           <Subscription subscription={customer.subscription[0]} margin="0" isUpdating={updatingGoogleExpDate} />
                        )}
                        <FlexWrapper justifyContent="flex-start" margin="0 0 1em">
                           <SwitchContainer>
                              <GrantPremiumOverrideLabel>Grant Premium Override</GrantPremiumOverrideLabel>
                              <Switch onClick={updateUserSubscription} isToggled={customer.premiumOverride} margin={'5px 0 0 0'} />{' '}
                              {customerWithoutSubBeingUpgraded && (
                                 <SmallLoaderContainer>
                                    <SmallLoader size={22} />
                                 </SmallLoaderContainer>
                              )}
                           </SwitchContainer>
                        </FlexWrapper>
                        {!!customer?.subscription.length && !subscriptionExpired && (
                           <>
                              <GrantPremiumAccessLabel isDisabled={!!customer.premiumOverride}>Grant premium access time</GrantPremiumAccessLabel>
                              <FlexWrapper justifyContent="flex-start">
                                 <Select
                                    //this is a hack to allow for a placeholder as well as a tracked value by the select
                                    placeholder={!premiumTimeToAdd ? 'Select time to add' : undefined}
                                    options={selectOptions}
                                    selectedOption={selectOptions.find(({ value }) => value === premiumTimeToAdd)}
                                    onChange={handlePremiumTimeSelect}
                                    isDisabled={!!customer.premiumOverride}
                                 />
                                 <Button disabled={!!customer.premiumOverride || !premiumTimeToAdd} onClick={() => setModalOpen(true)}>
                                    Add time
                                 </Button>
                              </FlexWrapper>
                           </>
                        )}
                     </FlexWrapper>
                  </SubscriptionContainer>
               </Section>
            </DetailList>
         )}
         {updateError && <ErrorMessage>Something went wrong when updating user subscription</ErrorMessage>}
      </ViewContainer>
   );
};

export default CustomerDetail;

const DetailList = styled(InfoList)`
   margin: unset;
   padding-left: 10px;
`;

const SubscriptionContainer = styled.div`
   display: flex;
   flex-flow: row wrap;
   justify-content: flex-start;
   align-items: center;
   text-align: left;
   max-width: 550px;
   width: 100%;
`;

const GrantPremiumAccessLabel = styled.label<{ isDisabled: boolean }>`
   font-weight: 600;
   color: ${({ theme, isDisabled }) => (isDisabled ? theme.primaryGrey : theme.secondaryBlueDark)};
   margin-bottom: 10px;
   transition-duration: 200ms;
`;

const GrantPremiumOverrideLabel = styled.label`
   font-weight: 600;
   color: ${({ theme }) => theme.secondaryBlueDark};
   margin-right: 7px;
`;

const Button = styled.button.attrs({ type: 'button' })`
   padding: 9px 11px;
   background-color: ${({ theme }) => theme.primaryBlueLight};
   border-radius: 10px;
   border: none;
   font-size: 1.5em;
   color: ${({ theme }) => theme.primaryWhite};
   font-weight: bold;
   margin-left: 1em;
   &:hover {
      cursor: pointer;
      background-color: ${({ theme }) => theme.primaryBlueDark};
   }
   &[disabled] {
      color: ${({ theme }) => theme.primaryGrey};
      background-color: ${({ theme }) => theme.disabled};
      cursor: default;
      border: 1px solid ${({ theme }) => theme.disabled};
   }
`;

const ModalContent = styled.div`
   margin: 3em auto 1em auto;
   padding: 10px;
   text-align: center;
   color: black;
   font-size: 1.3em;
   line-height: 1.6;
   min-width: 375px;
   @media only screen and (max-width: 415px) {
      font-size: 1.2em;
      padding: 9px;
   }
`;

const ExpiredNotification = styled.span`
   color: ${({ theme }) => theme.secondaryRed};
   font-size: 1em;
`;
const SwitchContainer = styled.div`
   display: flex;
   flex-direction: row;
   justify-content: flex-start;
   align-items: flex-start;
   margin-top: 10px;
`;

const Highlight = styled.span`
   font-weight: bold;
   color: ${({ theme }) => theme.secondaryBlueDark};
`;

const SmallLoaderContainer = styled.div`
   margin-bottom: 13px;
`;

const HeaderButtonContainer = styled.div`
   display: flex;
   justify-content: flex-start;
   flex-direction: column;
   align-items: flex-start;
   > * {
      margin: 5px 0;
   }
`;

const Section = styled.div`
   background-color: ${({ theme }) => theme.secondaryWhite};
   padding: 10px 15px;
   border-radius: 5px;
   border: 1px solid ${({ theme }) => theme.primaryGrey};
`;

const SectionHeader = styled.h3`
   font-weight: 700;
   color: ${({ theme }) => theme.primaryBlueDark};
   font-size: 1.6em;
   margin: 0;
`;

const UserActionButton = styled(ToggleButton)`
   font-size: 0.9em;
   font-weight: bold;
   background-color: ${({ theme }) => theme.primaryBlueLight};
   color: ${({ theme }) => theme.primaryWhite};
   min-width: 155px;
   &:hover {
      background-color: ${({ theme }) => theme.primaryBlueDark};
   }
`;
